import {COUNT_ACTIONS} from 'modules/cart/constants';
import {Counter} from 'components/Counter';
import React, {MouseEventHandler, useCallback} from 'react';
import {IProductShort, TProductCardVariant, TProductListView} from 'modules/products/types';
import {useCartProduct} from 'modules/cart/hooks/useCartProduct';
import style from './style.module.scss';
import classNames from 'classnames';
import {useAddToCartHandler} from 'modules/cart/hooks/useAddToCartHandler';
import {useAddToCartMutation, useGetCartQuery} from 'modules/cart/services';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {actionSetAddedProduct} from 'modules/cart/slice';
import {ICartProduct} from 'modules/cart/models';
import Icon from 'components/Icon';
import {CART_LINK} from 'core/modules/routing/constants';
import {useCartEventAnalytics} from 'modules/products/hooks/gtag/useCartEventAnalytics';
import {IAnalyticsMeta} from 'core/types';
import {ButtonNew} from 'components/ButtonNew';
import {TButtonSize} from 'components/Button';
import {useRouter} from 'next/router';
import {sendGtagEvent} from 'hooks/analytics/gtag/sendGtagEvent';
import {EventAction, EventCategory, EventLabel, EventNames} from 'modules/google-analytics/constants';

interface IProps extends IProductShort {
    showPrice: boolean;
    variant: TProductListView | TProductCardVariant;
    stretched: boolean;
    isHorizontal?: boolean;
    isStaticWidth?: boolean;
    btnSize?: TButtonSize;
    analytics?: IAnalyticsMeta;
}

const AddToCartButtonContent = ({inCart}: {inCart: boolean}) =>
    inCart ? <Icon className={style.productInCartArrow} icon={'arrow-m'} /> : <>В корзину</>;

export const AddToCartBlock = ({
    showPrice,
    variant,
    stretched,
    isHorizontal = false,
    isStaticWidth = false,
    btnSize,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    analytics,
    stockAmount,
    ...product
}: IProps) => {
    const router = useRouter();
    const dispatch = useAppDispatch();

    const {offerId} = product;

    const [updateCart] = useAddToCartMutation();
    const {data: cartData} = useGetCartQuery({mini: true});

    const cartProductsList = cartData?.products ?? [];

    // eslint-disable-next-line no-shadow
    const productInCart = cartProductsList.find((product) => product.offerId === Number(offerId));

    const {handleCartCountChange, cartProductCount} = useCartProduct({
        id: productInCart?.cartData?.id || 0,
        offerId,
        analytics,
    });

    const {createCartHandler: handleChangeQuantity} = useAddToCartHandler({
        cartProductsList,
        id: Number(offerId),
        product,
        analytics,
    });

    const cartEventGtag = useCartEventAnalytics();

    const handleAddToCartClick = useCallback<MouseEventHandler<HTMLElement>>(
        async (e) => {
            if (productInCart) {
                await router.push(CART_LINK);
            }
            const newId = e.currentTarget.getAttribute('data-offer-id');
            const response = await updateCart({
                offerId: Number(newId),
                quantity: 1,
            }).unwrap();

            const newProduct = response.products.find((item) => item.offerId === Number(newId));

            cartEventGtag(product as any, newProduct?.cartData?.quantity, analytics);

            if ('searchRecommend' === variant && analytics?.searchTerm) {
                sendGtagEvent({
                    event: EventNames.OWOX,
                    eventCategory: EventCategory.Search,
                    eventAction: EventAction.SearchSuggestAdd,
                    eventLabel: EventLabel.SearchSuggestAdd,
                    eventContext: [
                        {
                            term: analytics.searchTerm,
                        },
                    ],
                });
            }

            dispatch(actionSetAddedProduct(newProduct as ICartProduct));
        },

        [productInCart, updateCart, cartEventGtag, product, analytics, dispatch, router, variant],
    );

    const buttonSizeVariant = 'similar' === variant ? 'fixed-medium' : 'medium';

    return (
        <>
            {!Boolean(productInCart) ? (
                <ButtonNew
                    className={classNames(style.addToCartButton, style[`addToCartButton_${variant}`], {
                        [style.isNotEmpty]: Boolean(productInCart),
                    })}
                    isStretched={true}
                    data-offer-id={offerId}
                    onClick={handleAddToCartClick}
                    isSquare={Boolean(productInCart)}
                    variant={buttonSizeVariant}
                >
                    <AddToCartButtonContent inCart={Boolean(productInCart)} />
                </ButtonNew>
            ) : (
                <>
                    <div
                        className={classNames(style.goToCart, style[`goToCart_${variant}`], {
                            [style.visible]: 'searchRecommend' === variant,
                        })}
                    >
                        <ButtonNew
                            isSquare={true}
                            variants={{
                                DT: 'medium',
                                TB: 'medium',
                                MB: 'medium',
                            }}
                            href={CART_LINK}
                            leftIcon={<AddToCartButtonContent inCart={true} />}
                        />
                    </div>
                    <Counter
                        className={classNames(style.addToCartCounter, style[`addToCartCounter_${variant}`])}
                        count={cartProductCount}
                        handleAddClick={handleChangeQuantity({
                            action: COUNT_ACTIONS.ADD,
                            offerId,
                        })}
                        handleChange={handleCartCountChange}
                        handleRemoveClick={handleChangeQuantity({
                            action: COUNT_ACTIONS.REMOVE,
                            offerId,
                        })}
                        isHorizontal={isHorizontal}
                        isStaticWidth={isStaticWidth}
                        maxCount={stockAmount}
                    />
                </>
            )}
        </>
    );
};
